import JSZip from 'jszip';
import {
  getPacingGuideFileName,
  getPersonalizedPracticeFileName,
} from '../get-file-name';
import { COURSE_GRADE_MAPPING } from './constants';

export const NUMBER_OF_STRETCHES = 6;
export const NUMBER_OF_MODULES = 5;

export type DocumentParams = {
  courseId: string;
  documentType: string;
  hideStandards?: string;
};

export type FailedDownload = {
  stretch: string;
  module?: string;
  params: {
    courseId: string;
    documentType: string;
    stretch: string;
    module?: string;
    hideStandards?: string;
  };
};

export type DownloadProgress = {
  current: number;
  total: number;
  fileName: string;
};

export type DownloadResult = {
  success: boolean;
  errors: string[];
};

export type ProcessedDownloadResult = {
  success: boolean;
  failedDownloads: FailedDownload[];
  notification: {
    type: 'info' | 'error' | 'warning';
    text: string;
  } | null;
};

const getGradeName = (courseId: string): string => {
  return COURSE_GRADE_MAPPING[courseId] || courseId;
};

export const processDownloadResult = (
  result: DownloadResult,
  params: DocumentParams,
  documentType: string
): ProcessedDownloadResult => {
  if (result.success) {
    return {
      success: true,
      failedDownloads: [],
      notification: {
        type: 'info',
        text: 'All documents downloaded successfully',
      },
    };
  }

  if (!result.errors.length) {
    return {
      success: true,
      failedDownloads: [],
      notification: null,
    };
  }

  const failedDownloads = parseFailedDownloads(result.errors, params);
  const totalExpectedFiles =
    documentType === 'print-us-modules'
      ? NUMBER_OF_STRETCHES * NUMBER_OF_MODULES
      : NUMBER_OF_STRETCHES;

  return {
    success: false,
    failedDownloads,
    notification: {
      type: failedDownloads.length === totalExpectedFiles ? 'error' : 'warning',
      text:
        failedDownloads.length === totalExpectedFiles
          ? 'All files failed to download. Please try downloading them individually below.'
          : 'Some files were downloaded successfully. Check below for failed downloads.',
    },
  };
};

const parseFailedDownloads = (
  errors: string[],
  params: DocumentParams
): FailedDownload[] => {
  return errors
    .map((error) => {
      const match = error.match(/stretch (\d+)(?:, module (\d+))?/);
      if (!match) return null;

      const [, stretch, module] = match;
      return {
        stretch,
        ...(module && { module }),
        params: {
          ...params,
          stretch,
          ...(module && { module }),
        },
      };
    })
    .filter((item): item is FailedDownload => item !== null);
};

export const downloadSeparateFiles = async (
  documentType: string,
  params: DocumentParams,
  courseId: string,
  apolloServer: string,
  onProgress?: (progress: DownloadProgress) => void
): Promise<DownloadResult> => {
  const baseUrl = apolloServer.endsWith('/')
    ? apolloServer.slice(0, -1)
    : apolloServer;
  const zip = new JSZip();
  const stretches = Array.from(
    { length: NUMBER_OF_STRETCHES },
    (_, i) => i + 1
  );
  const errors: string[] = [];
  const total =
    documentType === 'print-us-modules'
      ? NUMBER_OF_STRETCHES * NUMBER_OF_MODULES
      : NUMBER_OF_STRETCHES;
  let current = 0;

  const updateProgress = (fileName: string): void => {
    current++;
    onProgress?.({
      current,
      total,
      fileName,
    });
  };

  if (documentType === 'print-us-modules') {
    const result = await downloadModuleFiles(
      stretches,
      zip,
      params,
      courseId,
      baseUrl,
      (fileName) => {
        updateProgress(fileName);
      }
    );
    errors.push(...result.errors);
  } else {
    const result = await downloadPacingGuideFiles(
      stretches,
      zip,
      params,
      courseId,
      baseUrl,
      (fileName) => {
        updateProgress(fileName);
      }
    );
    errors.push(...result.errors);
  }

  if (errors.length === 0 || errors.length < total) {
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const gradeName = getGradeName(courseId);
    triggerFileDownload(
      zipBlob,
      `${documentType}_${gradeName}-all-stretches.zip`
    );
  }

  return {
    success: errors.length === 0,
    errors,
  };
};

const downloadModuleFiles = async (
  stretches: number[],
  zip: JSZip,
  params: DocumentParams,
  courseId: string,
  baseUrl: string,
  onFileDownloaded?: (fileName: string) => void
): Promise<DownloadResult> => {
  const gradeName = getGradeName(courseId);
  const errors: string[] = [];

  for (const stretch of stretches) {
    const stretchFolder = zip.folder(`${gradeName}-stretch-${stretch}`);
    if (!stretchFolder) {
      errors.push(`Failed to create folder for stretch ${stretch}`);
      continue;
    }

    for (let module = 1; module <= NUMBER_OF_MODULES; module++) {
      const fileName = getPersonalizedPracticeFileName({
        courseId,
        stretch: stretch.toString(),
        module: module.toString(),
        hideStandards: params.hideStandards === 'true',
      });

      try {
        const response = await fetch(
          `${baseUrl}/api/download-us-documents?${new URLSearchParams({
            ...params,
            stretch: stretch.toString(),
            module: module.toString(),
          })}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to download stretch ${stretch}, module ${module}`
          );
        }

        const blob = await response.blob();
        stretchFolder.file(`${fileName}.pdf`, blob);
      } catch (error) {
        errors.push(`Failed to download stretch ${stretch}, module ${module}`);
        console.error(
          `Error downloading stretch ${stretch}, module ${module}:`,
          error
        );
      }
      onFileDownloaded?.(fileName);
    }
  }

  return {
    success: errors.length === 0,
    errors,
  };
};

const downloadPacingGuideFiles = async (
  stretches: number[],
  zip: JSZip,
  params: DocumentParams,
  courseId: string,
  baseUrl: string,
  onFileDownloaded?: (fileName: string) => void
): Promise<DownloadResult> => {
  const errors: string[] = [];

  for (const stretch of stretches) {
    const fileName = getPacingGuideFileName({
      courseId,
      stretch: stretch.toString(),
    });
    try {
      const response = await fetch(
        `${baseUrl}/api/download-us-documents?${new URLSearchParams({
          ...params,
          stretch: stretch.toString(),
        })}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`Failed to download stretch ${stretch}`);
      }

      const blob = await response.blob();
      zip.file(`${fileName}.pdf`, blob);
    } catch (error) {
      errors.push(`Failed to download stretch ${stretch}`);
      console.error(`Error downloading stretch ${stretch}:`, error);
    }
    onFileDownloaded?.(fileName);
  }

  return {
    success: errors.length === 0,
    errors,
  };
};

const triggerFileDownload = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
